import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DateFilter } from '@/components/DataTable/DateFilter'
import { useDateRange } from '@/components/DatePicker/hooks'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import { useDefaultTerritories, useQueryDefaults } from '@/hooks/queries/defaults/useQueryDefaults'
import { returnsSearchSchema, useReturns } from '@/hooks/queries/useOrders'
import { useTablePagination } from '@/hooks/useTablePagination'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { getNextWeekFriday } from '@repo/utils'
import { createFileRoute } from '@tanstack/react-router'
import { format, parse } from 'date-fns'
import { useTranslation } from 'react-i18next'
import ReturnsDataTable from './-components/ReturnsDataTable'

export const Route = createFileRoute('/_auth/_modules/_app/orders/returns/')({
  component: ReturnsPage,
  validateSearch: (search) => returnsSearchSchema.parse(search),
})

function ReturnsPage() {
  const { t } = useTranslation('orders')
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const { isDateInRange } = useDateRange({
    numberOfDaysBefore: 21,
    customEndDate: getNextWeekFriday(),
  })

  useQueryDefaults({
    search,
    navigate,
    defaults: {
      territory: useDefaultTerritories(),
      date: format(new Date(), 'yyyy-MM-dd'),
    },
  })

  const { isPending, error, data } = useReturns({
    ...search,
    page: search.page ?? 1,
    pageSize,
  })

  const onSelectDate = (date: Date | undefined) => {
    navigate({
      search: {
        ...search,
        date: date ? format(date, 'yyyy-MM-dd') : undefined,
        // Back to the first page, because the row the current page number refers to almost
        // certainly does not exist under the new date and the table would come back empty.
        page: 1,
      },
    })
  }

  return (
    <PageContainer maxWidth="wide">
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.RETURNS}
        title={t('tutorials.returns.dialog.title')}
        guideUrl="/orders/returns"
      >
        {t('tutorials.returns.dialog.description')}
      </IntroductionDialog>

      <Spread>
        <PageHeader>{t('returnsPage.heading')}</PageHeader>
        <DateFilter
          disabled={(date) => !isDateInRange(date)}
          defaultDate={search.date ? parse(search.date, 'yyyy-MM-dd', new Date()) : undefined}
          onSelect={onSelectDate}
        />
      </Spread>

      <Card>
        <CardContent className="py-6">
          <ReturnsDataTable
            isPending={isPending}
            error={error}
            data={data}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
